import React, { useState, useEffect } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import Column from './Column';
import GanttView from './GanttView';
import Timer from '../pomodoro/Timer';
import { Button } from '../ui/button';
import { Plus, Grid, ChartGantt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { PlanBadge } from '../subscription/PlanBadge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { CreateBoardDialog } from './CreateBoardDialog';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '../ui/skeleton';
import { TopNav } from '../navigation/TopNav';
import { WorkspaceSidebar } from '../navigation/WorkspaceSidebar';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';

interface Task {
  id: string;
  content: string;
  labels?: { type: 'urgent' | 'critical' | 'normal' | 'low' }[];
}

interface ColumnType {
  id: string;
  title: string;
  tasks: Task[];
}

const KanbanBoard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { canCreateBoard } = useSubscriptionContext();
  const { boardId } = useParams();
  const navigate = useNavigate();
  
  const [columns, setColumns] = useState<{ [key: string]: ColumnType }>({});
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'gantt'>('kanban');

  // First check if user is authenticated and get board info
  const { data: boardData, isLoading: isBoardLoading, error: boardError } = useQuery({
    queryKey: ['board', boardId],
    queryFn: async () => {
      try {
        if (!boardId) {
          navigate('/');
          return null;
        }

        const { data: board, error } = await supabase
          .from('boards')
          .select('*')
          .eq('id', boardId)
          .single();

        if (error) {
          console.error('Error fetching board:', error);
          throw error;
        }

        return board;
      } catch (error) {
        console.error('Detailed error in queryFn:', error);
        throw error;
      }
    },
  });

  // Then fetch columns and their tasks for the current board's workspace
  const { data: workspaceColumns, isLoading: isColumnsLoading, refetch: refetchColumns } = useQuery({
    queryKey: ['columns', boardData?.workspace_id, boardId],
    enabled: !!boardData?.workspace_id && !!boardId,
    queryFn: async () => {
      try {
        console.log('Fetching columns for board:', boardId);
        const { data: columns, error } = await supabase
          .from('columns')
          .select(`
            *,
            tasks:tasks(
              id,
              content,
              position,
              start_date,
              due_date,
              labels:task_labels(*)
            )
          `)
          .eq('board_id', boardId)
          .order('position');

        if (error) {
          console.error('Error fetching columns:', error);
          throw error;
        }

        console.log('Fetched columns:', columns);
        return columns || [];
      } catch (error) {
        console.error('Error fetching columns:', error);
        throw error;
      }
    },
  });

  useEffect(() => {
    if (!currentWorkspaceId) return;

    // Subscribe to changes in columns
    const columnsChannel = supabase
      .channel('columns-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'columns',
          filter: `workspace_id=eq.${currentWorkspaceId}`,
        },
        () => {
          refetchColumns();
        }
      )
      .subscribe();

    // Subscribe to changes in tasks
    const tasksChannel = supabase
      .channel('tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
        },
        () => {
          refetchColumns();
        }
      )
      .subscribe();

    // Subscribe to changes in task labels
    const labelsChannel = supabase
      .channel('labels-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_labels',
        },
        () => {
          refetchColumns();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(columnsChannel);
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(labelsChannel);
    };
  }, [currentWorkspaceId, refetchColumns]);

  useEffect(() => {
    if (boardData) {
      setCurrentWorkspaceId(boardData.workspace_id);
    }
  }, [boardData]);

  useEffect(() => {
    if (workspaceColumns) {
      const formattedColumns = workspaceColumns.reduce((acc, column) => {
        acc[column.id] = {
          id: column.id,
          title: column.title,
          tasks: column.tasks?.map((task: any) => ({
            id: task.id,
            content: task.content,
            start_date: task.start_date,
            due_date: task.due_date,
            labels: task.labels?.map((label: any) => ({
              type: label.type
            }))
          })) || [],
        };
        return acc;
      }, {} as { [key: string]: ColumnType });

      setColumns(formattedColumns);
    }
  }, [workspaceColumns]);

  const onDragEnd = async (result: any) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];
    const task = sourceColumn.tasks.find(t => t.id === draggableId);

    if (!task) return;

    try {
      // Update task's column and position in the database
      const { error: updateError } = await supabase
        .from('tasks')
        .update({
          column_id: destination.droppableId,
          position: destination.index,
          updated_at: new Date().toISOString()
        })
        .eq('id', draggableId);

      if (updateError) throw updateError;

      // Update local state
      const newSourceTasks = Array.from(sourceColumn.tasks);
      newSourceTasks.splice(source.index, 1);

      const newDestTasks = Array.from(destColumn.tasks);
      newDestTasks.splice(destination.index, 0, task);

      const newColumns = {
        ...columns,
        [source.droppableId]: {
          ...sourceColumn,
          tasks: newSourceTasks,
        },
        [destination.droppableId]: {
          ...destColumn,
          tasks: newDestTasks,
        },
      };

      setColumns(newColumns);
    } catch (error) {
      console.error('Error updating task position:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorUpdatingTask'),
        variant: 'destructive',
      });
    }
  };

  const handleAddColumn = async () => {
    if (!currentWorkspaceId || !boardId) {
      toast({
        title: t('common.error'),
        description: t('common.noWorkspaceSelected'),
        variant: 'destructive',
      });
      return;
    }

    const canCreate = await canCreateBoard(currentWorkspaceId);
    
    if (!canCreate) {
      toast({
        title: t('common.limitReached'),
        description: t('common.upgradeToAddMore'),
        variant: 'destructive',
      });
      return;
    }

    try {
      console.log('Adding new column to board:', boardId);
      const position = Object.keys(columns).length;
      const { data: newColumn, error } = await supabase
        .from('columns')
        .insert([
          {
            title: t('common.newColumn'),
            position,
            workspace_id: currentWorkspaceId,
            board_id: boardId
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating column:', error);
        throw error;
      }

      if (newColumn) {
        console.log('New column created:', newColumn);
        setColumns(prev => ({
          ...prev,
          [newColumn.id]: {
            id: newColumn.id,
            title: newColumn.title,
            tasks: [],
          },
        }));
        
        toast({
          title: t('common.success'),
          description: t('common.columnCreated'),
        });
      }
    } catch (error) {
      console.error('Error creating column:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorCreatingColumn'),
        variant: 'destructive',
      });
    }
  };

  if (boardError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">{t('common.errorLoadingBoard')}</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          {t('common.backToDashboard')}
        </Button>
      </div>
    );
  }

  if (isBoardLoading || isColumnsLoading) {
    return (
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="flex gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[500px] w-80" />
          ))}
        </div>
      </div>
    );
  }

  if (!boardData) {
    return (
      <div className="p-8 text-center">
        <p>{t('common.boardNotFound')}</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          {t('common.backToDashboard')}
        </Button>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full">
        <div className="fixed top-0 left-0 right-0 z-50">
          <TopNav />
        </div>
        <div className="flex pt-14">
          <WorkspaceSidebar />
          <SidebarInset className="w-full">
            <div className="flex flex-col h-screen bg-white dark:bg-slate-950 p-6">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                  <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
                    {boardData.name}
                  </h1>
                  <PlanBadge />
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant={viewMode === 'kanban' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('kanban')}
                    >
                      <Grid className="h-4 w-4 mr-2" />
                      Kanban
                    </Button>
                    <Button
                      variant={viewMode === 'gantt' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('gantt')}
                    >
                      <ChartGantt className="h-4 w-4 mr-2" />
                      Gantt
                    </Button>
                  </div>
                </div>
                <Timer />
              </div>
              
              {viewMode === 'kanban' ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className="flex gap-6 overflow-x-auto pb-8 px-2">
                    {Object.values(columns).map((column) => (
                      <Column key={column.id} column={column} />
                    ))}
                    <Button
                      variant="outline"
                      className="h-[500px] w-80 flex items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-primary hover:bg-primary/5"
                      onClick={handleAddColumn}
                    >
                      <Plus className="mr-2" />
                      {t('common.addColumn')}
                    </Button>
                  </div>
                </DragDropContext>
              ) : (
                <GanttView columns={columns} />
              )}
            </div>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default KanbanBoard;